import { zodResolver } from '@hookform/resolvers/zod';
import { trackAnalyticsEvent } from '@pm/analytics';
import { FormError, FormField, FormSubmitButton } from '@pm/forms';
import { ButtonLink, Input, useToast } from '@pm/ui';
import { type ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { z } from 'zod';
import { useAuth } from '../../context/auth';
import { detectUserErrors } from '../../modules/errors/errors';
import { toServerError } from '../../utilities/errors';
import { PasswordVisibilityField } from '../PasswordVisibilityField/PasswordVisibilityField';

type LoginFormProps = {
  // Slot for the Google SSO button — kept app-specific until the Firebase SSO
  // branch lands, at which point it moves here with no GoogleOAuthProvider dependency.
  googleButton?: ReactNode;
  // Slot for the magic link button — callers check the magicLinkLogin flag and
  // pass <MagicLinkButton> so the flag mock reaches it in tests.
  magicLinkButton?: ReactNode;
  submitLabel?: string;
  helpLink?: ReactNode;
};

export const LoginForm = ({
  googleButton,
  magicLinkButton,
  submitLabel,
  helpLink,
}: LoginFormProps) => {
  const { t } = useTranslation('global', { keyPrefix: 'Login' });
  const { useLogin } = useAuth();
  const [loginMutation] = useLogin();
  const { search } = useLocation();
  const { positive } = useToast();

  const showAlternativeLogins = !!googleButton || !!magicLinkButton;

  const schema = z.object({
    email: z.string().min(1, t('MissingEmail')).email(t('InvalidEmail')),
    password: z.string().min(1, t('MissingPassword')),
  });

  type LoginSchema = z.infer<typeof schema>;

  const methods = useForm<LoginSchema>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { email: '', password: '' },
  });

  return (
    <main className="flex flex-col mr-auto w-full">
      <FormProvider {...methods}>
        <form
          className="space-y-xxl"
          onSubmit={methods.handleSubmit(async ({ email, password }) => {
            try {
              const { data: loginData } = await loginMutation({
                variables: {
                  input: {
                    email,
                    password,
                  },
                },
              });

              detectUserErrors(loginData, (response) => response.login?.errors);

              trackAnalyticsEvent('Log in', { method: 'email_password' });
              positive({ content: t('LoginSuccess') });
            } catch {
              methods.setError(...toServerError('Unable to login'));
            }
          })}
        >
          <div className="space-y-l">
            <FormField
              id="email"
              type="email"
              label={t('Email')}
              name="email"
              placeholder="example@example.com"
              space="xxs"
              Component={Input}
            />
            <FormField
              id="password"
              type="password"
              label={t('Password')}
              name="password"
              placeholder={t('Password')}
              autoComplete="current-password"
              space="xxs"
              Component={PasswordVisibilityField}
            />
            <ButtonLink
              to={{ pathname: '/password/forgot', search }}
              intent="tertiary"
              width="fit"
              size="xSmall"
            >
              {t('Forgot')}
            </ButtonLink>
          </div>
          <FormSubmitButton width="full">
            {submitLabel ?? t('SignIn')}
          </FormSubmitButton>
          <FormError
            error={{
              instructions: t('IncorrectPassword'),
            }}
          />
        </form>
      </FormProvider>
      {showAlternativeLogins && (
        <div>
          <div className="my-xl flex items-center gap-s">
            <hr className="flex-1 border-content-disabled" />
            <span className="text-content-subdued text-sm">{t('Or')}</span>
            <hr className="flex-1 border-content-disabled" />
          </div>
          <div className="space-y-xs">
            {googleButton}
            {magicLinkButton}
          </div>
        </div>
      )}
      {helpLink && <div className="mt-xxs flex justify-center">{helpLink}</div>}
    </main>
  );
};
